import React, {useState} from 'react';
import {Button, Checkbox, Input, Modal, Spinner} from "@tag/tag-components-react-v4";
import {useNavigate} from "react-router-dom";
import {CustomerDTO} from "../types/CustomerDTO";
import CustomerService from "../services/CustomerService";
import { ProjectAccents } from "../../General/config/projectAccents";
import PipelineStatusDisplay from "../../General/components/PipelineStatusDisplay";

interface RetrieveDbBackupsResult {
    buildUrl?: string;
    buildId?: number;
    pipelineId?: number;
}

function RetrieveDbBackupsModal(props: Readonly<{
    visible: boolean,
    selectedCustomer: CustomerDTO,
    handleClose: () => void,
    handleCustomerMenuModalClose: Function,
    setAlert: Function
}>) {
    const [includeAuditDb, setIncludeAuditDb] = useState(false);
    const [includeExternalDb, setIncludeExternalDb] = useState(false);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [pipelineResult, setPipelineResult] = useState<RetrieveDbBackupsResult>();
    const [pipelineComplete, setPipelineComplete] = useState(false);
    const navigate = useNavigate();

    const isValid = reason.trim().length > 0;

    const handleConfirm = async () => {
        setLoading(true);

        try {
            const result = await CustomerService.retrieveDbBackups(
                props.selectedCustomer.clientId,
                includeAuditDb,
                includeExternalDb,
                reason,
                props.selectedCustomer.environmentName
            );

            if (result.success) {
                setPipelineResult({
                    buildUrl: result.buildUrl,
                    buildId: result.buildId,
                    pipelineId: result.pipelineId
                });
            } else {
                props.setAlert({type: 'error', message: result.errorMessage || 'Failed to trigger retrieve DB backups pipeline'});
                props.handleClose();
            }
        } catch (e: any) {
            props.setAlert({type: 'error', message: e.message || 'An error has occurred, please contact HSC Care DevOps'});
            props.handleClose();
        } finally {
            setLoading(false);
        }
    };

    const handleGoToBackups = () => {
        props.handleClose();
        props.handleCustomerMenuModalClose();
        navigate('/peopleplanner/dbbackups');
    };

    const renderModalContent = () => {
        if (loading) {
            return (
                <div style={{display: 'flex', alignItems: 'center', gap: '1em', padding: '1em 0'}}>
                    <Spinner device="touch" />
                    <span>Triggering retrieve DB backups pipeline...</span>
                </div>
            );
        }

        if (pipelineResult) {
            return (
                <>
                    <p>Pipeline triggered successfully for <strong>{props.selectedCustomer.name}</strong>!</p>
                    {pipelineResult.pipelineId && pipelineResult.buildId ? (
                        <PipelineStatusDisplay
                            apiBaseUrl={process.env.REACT_APP_SELF_SERVICE_PEOPLEPLANNER_API_URL}
                            pipelineId={pipelineResult.pipelineId}
                            runId={pipelineResult.buildId}
                            buildUrl={pipelineResult.buildUrl}
                            onComplete={(result) => setPipelineComplete(result === 'succeeded')}
                        />
                    ) : pipelineResult.buildUrl ? (
                        <p>
                            <a href={pipelineResult.buildUrl} target="_blank" rel="noopener noreferrer">
                                View Pipeline Run
                            </a>
                        </p>
                    ) : pipelineResult.buildId ? (
                        <p>Pipeline Run ID: <strong>{pipelineResult.buildId}</strong></p>
                    ) : null}
                    {pipelineComplete && (
                        <div style={{display: 'flex', gap: '1em', marginTop: '1em'}}>
                            <Button text={'Go to DB backups'} onClick={handleGoToBackups} />
                        </div>
                    )}
                </>
            );
        }

        return (
            <div style={{marginTop: '16px'}}>
                <div style={{marginBottom: '12px', display: 'flex', alignItems: 'center'}}>
                    <Checkbox
                        checked={includeAuditDb}
                        onChange={(value) => setIncludeAuditDb(value.checked)}
                    />
                    <span style={{marginLeft: '8px'}}>Include Audit DB</span>
                </div>
                <div style={{marginBottom: '12px', display: 'flex', alignItems: 'center'}}>
                    <Checkbox
                        checked={includeExternalDb}
                        onChange={(value) => setIncludeExternalDb(value.checked)}
                    />
                    <span style={{marginLeft: '8px'}}>Include External DB</span>
                </div>
                <div style={{marginBottom: '12px'}}>
                    <Input
                        placeholder="Reason (required)"
                        value={reason}
                        onChange={(e) => setReason(e.currentTarget.value)}
                        style={{width: '100%'}}
                    />
                </div>
                <div style={{display: 'flex', gap: '1em', marginTop: '1em'}}>
                    <Button text={'Confirm'} disabled={!isValid} onClick={handleConfirm} />
                </div>
            </div>
        );
    };

    return (
        <Modal
            visible={props.visible}
            accent={ProjectAccents.PeoplePlanner}
            heading={pipelineResult
                ? 'Retrieve DB Backups Triggered'
                : `Retrieve DB backups for '${props.selectedCustomer.name}'`}
            onCancelButtonClick={() => props.handleClose()}
            onClose={() => props.handleClose()}
            primaryButtonProps={{buttonStyle: {'display': 'none'}}}
        >
            <div>
                {renderModalContent()}
            </div>
        </Modal>
    );
}

export default RetrieveDbBackupsModal;
